import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  Badge,
  Button,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { CHANGE_USER_STATUS, DEL_USER } from "apollo/mutuations";
import { GET_ALL_SUPPLIERS, GET_ALL_USERS } from "apollo/queries";
import B2bTable from "components/reusable/b2bTable";
import SupplierImport from "components/Supplier/SupplierImport";
import UserEditModal from "components/User/UserEditModal";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";
import { Download, Upload } from "tabler-icons-react";
import ExportComponent from "utiles/export";
import { formatNumber } from "utiles/url";

const Users = () => {
  const [size, setSize] = useState("10");
  const [opened, setOpened] = useState(false);
  const [openedDelete, setOpenedDelete] = useState(false);
  const [openedStatusChange, setStatusChange] = useState(false);
  const [openedEdit, setOpenedEdit] = useState(false);
  const [openedImport, setOpenedImport] = useState(false);
  const [editId, setEditId] = useState();
  const [deleteID, setDeleteID] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [status, setStatus] = useState(null);
  const [total, setTotal] = useState(0);

  const { data, loading, refetch } = useQuery(GET_ALL_SUPPLIERS, {
    variables: {
      first: parseInt(size),
      page: activePage,
      is_sme:false,
      search: searchValue,
      ordered_by: [
        {
          column: "CREATED_AT",
          order: "DESC",
        },
      ],
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Get Suppliers",
      });
    },
  });

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };
  useEffect(() => {
    if (data) {
      setTotal(data.getSuppliers.paginatorInfo.lastPage);
    }
  }, [data, size]);
  useEffect(() => {
    refetch();
  }, []);

  const [delUser] = useMutation(DEL_USER, {
    update(cache, { data: { deleteUser } }) {
      cache.updateQuery(
        {
          query: GET_ALL_USERS,
          variables: {
            first: 10,
            page: activePage,
            search: "",
          },
        },
        (data) => {
          if (data.getSuppliers.data.length === 1) {
            setTotal(total - 1);
            setActivePage(activePage - 1);
          } else {
            return {
              getSuppliers: {
                data: [
                  ...data.getSuppliers.data.filter(
                    (user) => user.id !== deleteUser.id,
                  ),
                ],
              },
            };
          }
        },
      );
    },
  });

  const theme = useMantineTheme();

  const headerData = [
    {
      label: "#",
      key: "index",
      sortable: false,
      searchable: false,
      width: "5%",
      render: (_rowData, rowIndex) => {
        const idx = (activePage - 1) * parseInt(size || "0") + (rowIndex + 1);
        return <span>{idx}</span>;
      },
    },
    {
      label: "Name",
      key: "name_user",
      sortable: true,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.user.name}</span>;
      },
    },
    {
      label: "Phone",
      key: "Phone",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.user.phone}</span>;
      },
    },
    {
      label: "Register By",
      key: "Phone",
      exportable: false,
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.registeredBy?.name}</span>;
      },
    },
    {
      label: "Status",
      key: "account",
      sortable: false,
      exportable: false,
      searchable: false,
      render: (rowData) => {
        return (
          <span>
            {rowData.user.status === "ACTIVE" ? (
              <Badge variant="light" color="green">
                Active
              </Badge>
            ) : (
              <Badge variant="light" color="red">
                Not Active
              </Badge>
            )}
          </span>
        );
      },
    },
    {
      label: "Total Balance",
      key: "total_balance",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{formatNumber(rowData.total_balance)}</span>;
      },
    },
    {
      label: "Total Withdrawal",
      key: "total_withdrawal",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{formatNumber(rowData.total_withdrawal)}</span>;
      },
    },
  ];
  const [changeUserStatus] = useMutation(CHANGE_USER_STATUS, {
    refetchQueries: [
      {
        query: GET_ALL_USERS,
        variables: {
          first: 10,
          page: activePage,
        },
      },
    ],
    onCompleted(data) {
      const action =
        data.changeUserStatus.status === "ACTIVE" ? "Activated" : "Deactivated";
      showNotification({
        color: "green",
        title: "Success",
        message: `User ${action} successfully`,
      });
      setStatusChange(false);
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message: `${error.message}`,
      });
    },
  });

  const handleUserStatusChange = () => {
    changeUserStatus({
      variables: {
        id: deleteID, // Ensure id is an integer
        status: status, // Toggle the status
      },
    });
  };

  const handleDelete = (id) => {
    setOpenedDelete(true);
    setDeleteID(id);
  };
  const handleStatus = (id, status) => {
    setStatusChange(true);
    setDeleteID(id);
    setStatus(status);
  };

  const handleEditUser = (id) => {
    setOpenedEdit(true);
    setEditId(id);
  };

  const handleChange = (currentPage) => {
    setActivePage(currentPage);
  };

  const deleteUser = () => {
    delUser({
      variables: { id: deleteID },
      refetchQueries: [
        {
          query: GET_ALL_USERS,
          variables: {
            first: 10,
            page: 1,
          },
        },
      ],
      onCompleted(data) {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "green",
          title: "Success",
          message: "User Deleted Successfully",
        });
      },
      onError(error) {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "red",
          title: "Error",
          message: `${error}`,
        });
      },
    });
  };
  const [confirmedSearch, setConfirmedSearch] = useState("");

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };
  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };
  const [fetchAllSuppliers] = useLazyQuery(GET_ALL_SUPPLIERS);

  const handleExportDataFetch = async () => {
    const { data: allData } = await fetchAllSuppliers({
      variables: {
        first: null, // This tells the backend to skip pagination limits
        page: 1,
        is_sme: false,
        search: searchValue,
        ordered_by: [{ column: "CREATED_AT", order: "DESC" }],
      },
    });
    return allData?.getSuppliers?.data || [];
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/suppliers.xlsx";
    link.download = "suppliers_template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Drawer
        opened={openedEdit}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        styles={{
          closeButton: {
            color: "black",
            marginTop: "50px",
          },
        }}
        padding="xl"
        onClose={() => setOpenedEdit(false)}
        position="right"
        size="40%"
      >
        <UserEditModal
          setOpenedEdit={setOpenedEdit}
          editId={editId}
          data={data ? data.getSuppliers.data : []}
        />
      </Drawer>
      <Modal
        opened={openedDelete}
        onClose={() => setOpenedDelete(false)}
        title="Warning"
        centered
      >
        <p>Are you sure do you want todelete this user?</p>
        <Group position="right">
          <Button onClick={() => deleteUser()} color="red">
            Delete
          </Button>
        </Group>
      </Modal>
      <Modal
        opened={openedStatusChange}
        onClose={() => setStatusChange(false)}
        title="Warning"
        centered
      >
        <p>{`Are you sure do you want to ${
          status === "ACTIVE" ? "Activat" : "Deactivat"
        } this  user?`}</p>
        <Group position="right">
          <Button onClick={() => handleUserStatusChange()} color="red">
            {status === "ACTIVE" ? "Activat" : "Deactivat"}
          </Button>
        </Group>
      </Modal>
      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            handleChange={handleChange}
            paginatorInfo={data?.getSuppliers?.paginatorInfo}
            header={headerData}
            clearInput={clearInput}
            handelSearch={handleManualSearch}
            filterData={({ onCardClick }) => (
              <Group
                spacing="xs"
                style={{ display: "flex", flexWrap: "nowrap" }}
              >
                <Button
                  style={{
                    backgroundColor: "#FF6A00",
                    color: "#FFFFFF",
                  }}
                  onClick={handleDownloadTemplate}
                  leftIcon={<Download size={16} />}
                >
                  Download
                </Button>
                <Button
                  style={{
                    backgroundColor: "#FF6A00",
                    color: "#FFFFFF",
                  }}
                  onClick={() => setOpenedImport(true)}
                  leftIcon={<Upload size={16} />}
                >
                  Import
                </Button>
                <ExportComponent
                  data={data ? data.getSuppliers.data : []}
                  headers={headerData}
                  reportTitle="Supplier Report"
                  onFetch={handleExportDataFetch}
                  fileNamePrefix="suppliers_report"
                />
              </Group>
            )}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            loading={loading}
            data={data ? data.getSuppliers.data : []}
            size={size}
            handlePageSizeChange={handlePageSizeChange}
          />
        </ScrollArea>
      </Card>

      <SupplierImport
        opened={openedImport}
        onClose={() => setOpenedImport(false)}
        onSuccess={() => {
          refetch();
        }}
      />
    </div>
  );
};

export default Users;
